import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Search,
  Compass,
  Code2,
  Rocket,
  GraduationCap,
  Headphones,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Diagnóstico",
    desc: "Imersão nos processos, dados e desafios da sua instituição — mapeamento completo do fluxo assistencial, administrativo e financeiro.",
  },
  {
    n: "02",
    icon: Compass,
    title: "Planejamento",
    desc: "Roadmap tecnológico com escopo, entregáveis e indicadores claros. Cada projeto começa com prioridades bem definidas.",
  },
  {
    n: "03",
    icon: Code2,
    title: "Desenvolvimento",
    desc: "Construção iterativa com entregas incrementais, validações contínuas e integração nativa ao ecossistema Tasy.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Implantação",
    desc: "Go-live assistido, integrações validadas e migração segura — com plano de contingência e monitoramento 24/7.",
  },
  {
    n: "05",
    icon: GraduationCap,
    title: "Treinamento",
    desc: "Capacitação de equipes clínicas, administrativas e de TI. Material didático, sessões práticas e trilhas por perfil.",
  },
  {
    n: "06",
    icon: Headphones,
    title: "Suporte",
    desc: "Operação assistida e evolução contínua com SLA definido. Time consultivo dedicado para ambientes críticos.",
  },
];

export default function HowWeWork() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={containerRef}
      data-testid="how-section"
      className="relative bg-white py-24 lg:py-32 border-y border-gray-200 overflow-hidden"
    >
      {/* Ambient */}
      <div
        aria-hidden
        className="absolute top-1/3 right-0 w-96 h-96 rounded-full bg-[#3B82F6]/5 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-24"
        >
          <div className="lg:col-span-5">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#3B82F6] mb-4">
              /04 · Nossa metodologia
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Como <span className="text-[#1E3A8A]">Trabalhamos</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              Um processo consultivo e transparente em seis etapas — desenhado
              para reduzir riscos, acelerar entregas e maximizar valor clínico
              e operacional.
            </p>
          </div>
        </motion.div>

        {/* Timeline with animated progress line */}
        <div className="relative">
          {/* Progress line — desktop */}
          <div className="hidden lg:block absolute left-[calc(8.333%_-_1px)] top-0 bottom-0 w-[2px] bg-gray-200">
            <motion.div
              style={{ height: lineHeight }}
              className="w-full bg-gradient-to-b from-[#3B82F6] via-[#3B82F6] to-[#1E3A8A]"
            />
          </div>
          {/* Progress line — mobile */}
          <div className="lg:hidden absolute left-[19px] top-0 bottom-0 w-[2px] bg-gray-200">
            <motion.div
              style={{ height: lineHeight }}
              className="w-full bg-gradient-to-b from-[#3B82F6] via-[#3B82F6] to-[#1E3A8A]"
            />
          </div>

          <ol className="space-y-8 lg:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.n}
                  data-testid={`step-${s.n}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="relative grid grid-cols-12 gap-4 lg:gap-10 group"
                >
                  {/* Dot */}
                  <div className="col-span-1 relative">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.5,
                        delay: 0.2,
                        ease: [0.34, 1.56, 0.64, 1],
                      }}
                      className="absolute left-2.5 lg:left-[calc(0%_-_11px)] top-4 w-6 h-6 rounded-full bg-white border-2 border-[#3B82F6] flex items-center justify-center z-10"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]" />
                    </motion.div>
                  </div>

                  {/* Number & icon */}
                  <div className="col-span-11 lg:col-span-3 pl-8 lg:pl-8">
                    <div className="flex items-center gap-4">
                      <span className="font-display text-5xl lg:text-7xl font-black text-gray-100 group-hover:text-[#3B82F6]/20 transition-colors tabular-nums">
                        {s.n}
                      </span>
                      <div className="w-11 h-11 bg-[#111827] text-white flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                        <Icon className="w-5 h-5" strokeWidth={1.75} />
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="col-span-12 lg:col-span-4 pl-8 lg:pl-0">
                    <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight text-[#111827] group-hover:text-[#1E3A8A] transition-colors">
                      {s.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <div className="col-span-12 lg:col-span-4 pl-8 lg:pl-0">
                    <p className="text-base text-gray-600 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
